import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { History, ChevronDown, RefreshCw } from "lucide-react";

import { usePersistedOpen } from "@/lib/usePersistedOpen";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = {
  create: { label: "Added", cls: "text-bt-positive border-bt-positive/30 bg-bt-positive/10" },
  delete: { label: "Deleted", cls: "text-bt-negative border-bt-negative/30 bg-bt-negative/10" },
  role_change: { label: "Role change", cls: "text-bt-gold border-bt-gold/30 bg-bt-gold/10" },
  lock: { label: "Locked", cls: "text-bt-negative border-bt-negative/30 bg-bt-negative/10" },
  unlock: { label: "Unlocked", cls: "text-bt-positive border-bt-positive/30 bg-bt-positive/10" },
  password_reset: { label: "Password reset", cls: "text-bt-text-secondary border-bt-border-subtle bg-bt-surface-2" },
  welcome_sent: { label: "Welcome email", cls: "text-bt-gold border-bt-gold/30 bg-bt-gold/10" },
  reminder_sent: { label: "Welcome reminder", cls: "text-bt-gold border-bt-gold/30 bg-bt-gold/10" },
  stock_add: { label: "Stock added", cls: "text-bt-positive border-bt-positive/30 bg-bt-positive/10" },
  stock_remove: { label: "Stock removed", cls: "text-bt-negative border-bt-negative/30 bg-bt-negative/10" },
  trade_cleanup: { label: "Trade cleanup", cls: "text-bt-negative border-bt-negative/30 bg-bt-negative/10" },
  winback_sent: { label: "Where have you been", cls: "text-bt-gold border-bt-gold/30 bg-bt-gold/10" },
};

export default function AdminAuditLog({ refreshKey }) {
  const [open, setOpen] = usePersistedOpen("audit_log", false);
  const [filter, setFilter] = useState("");
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const q = filter ? `?action=${filter}` : "";
      const { data } = await axios.get(`${BACKEND}/api/admin/audit-log${q}`, { withCredentials: true });
      setRows(data);
    } catch { setRows([]); }
  }, [filter]);

  useEffect(() => { if (open) load(); }, [open, load, refreshKey]);

  return (
    <div className="bg-bt-surface border border-bt-border-subtle mb-6" data-testid="admin-audit-log">
      <button onClick={() => setOpen(o => !o)} data-testid="toggle-audit-log"
        className="w-full px-4 py-3 border-b border-bt-border-subtle flex items-center justify-between hover:bg-bt-surface-2/40 transition-colors">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
          <span className="text-xs font-mono uppercase tracking-wider text-bt-text-secondary">User Activity Log</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-bt-text-tertiary transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter"
              className="h-8 px-2 text-xs font-mono bg-bt-surface-2 border border-bt-border-subtle text-bt-text-primary">
              <option value="">All actions</option>
              {Object.entries(ACTIONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={load} data-testid="audit-refresh" title="Refresh"
              className="h-8 px-2 border border-bt-border-subtle text-bt-text-secondary hover:text-bt-text-primary">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <span className="text-[11px] font-mono text-bt-text-tertiary ml-auto">{rows.length} entries</span>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="text-xs font-mono text-bt-text-tertiary py-6 text-center" data-testid="audit-empty">No activity recorded yet.</p>
            ) : (
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-bt-text-tertiary uppercase tracking-wider text-left">
                    <th className="py-2 pr-3 font-normal">When</th><th className="py-2 pr-3 font-normal">Action</th>
                    <th className="py-2 pr-3 font-normal">User</th><th className="py-2 pr-3 font-normal">Details</th>
                    <th className="py-2 font-normal">By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const a = ACTIONS[r.action] || { label: r.action, cls: "text-bt-text-secondary border-bt-border-subtle" };
                    return (
                      <tr key={i} className="border-t border-bt-border-subtle" data-testid="audit-row">
                        <td className="py-2 pr-3 text-bt-text-tertiary whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                        <td className="py-2 pr-3"><span className={`px-1.5 py-0.5 border whitespace-nowrap ${a.cls}`}>{a.label}</span></td>
                        <td className="py-2 pr-3 text-bt-text-primary">{r.target}</td>
                        <td className="py-2 pr-3 text-bt-text-secondary">{r.details || "—"}</td>
                        <td className="py-2 text-bt-text-tertiary">{r.admin}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
